/* eslint-disable react/sort-comp */
import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import {
  Container,
  GridSelector,
  GridSelectorItem,
  Icon,
} from '@scentregroup/ui'
import PropTypes from 'prop-types'
import Fuse from 'fuse.js'
import containerStyle from '../container.scss'
import analyticsUtils from '../../helpers/analytics-utils'
import getNearestCentres from '../../helpers/get-nearest-centres'
import TabsHeader from '../tabs/tabs-header'
import CentreSelectorDevice from './centre-selector-device'
import styles from './index.scss'

class CentreSelector extends Component {
  // As NZ only has 4 centres, here we are over-riding the tab UI/behaviour
  state = {
    centres: this.props.centres,
    closestCentres: [],
    searchString: '',
    loading: false,
    currentTab: this.props.isNZ ? 1 : null,
    disabledTabVal: '',
  }

  handleSearchStarted = () => {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_START_SEARCH,
    })
  }

  handleCentreSelect = centre => {
    const { searchString } = this.state
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
      eventAction: analyticsUtils.actions.SEARCH_RESULT_ClICKED,
      eventLabel: searchString,
      centreResultClicked: centre.title,
    })
  }

  updateSearchString = e => {
    this.setState({ searchString: e.target.value })
    const options = {
      shouldSort: true,
      threshold: 0.3,
      location: 0,
      keys: ['title', 'suburb', 'state'],
    }
    const fuse = new Fuse(this.props.centres, options)
    if (e.target.value) {
      const foundCentres = fuse.search(e.target.value)
      this.setState({ centres: foundCentres })
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
        eventAction: analyticsUtils.actions.SEARCH_SHOWN,
        eventLabel: e.target.value,
        noReturnedResults: foundCentres.length,
      })
    } else {
      this.setState({ centres: this.props.centres })
    }
  }
  handleKeyPress = e => {
    if (e.key === 'Enter') {
      window.document.activeElement.blur()
    }
  }
  geolocationDisabled = () => {
    // Pass google analytics error
    this.setState({
      loading: false,
      currentTab: 1,
      disabledTabVal: 'Nearby',
    })
  }
  centreNearMe = () => {
    this.setState({
      loading: true,
    })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_SET_LOCATION,
    })
    const setNearbyCentres = (userLat, userLon) => {
      this.setState({
        closestCentres: getNearestCentres(userLat, userLon, this.state.centres),
        loading: false,
      })
    }

    try {
      // For browsers that do not support geolocation
      navigator.geolocation.getCurrentPosition(function(position) {
        setNearbyCentres(position.coords.latitude, position.coords.longitude)
      }, this.geolocationDisabled)
    } catch (e) {
      this.geolocationDisabled(e)
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
        eventAction: analyticsUtils.actions.CLICK_TO_SET_LOCATION_DENIED,
      })
    }
  }
  showAllCentres = () => {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_SEE_ALL_CENTRES,
    })
  }
  tabBtn = e => {
    const activeTab = Number(e.target.getAttribute('index'))
    const { currentTab } = this.state
    if (activeTab === currentTab) {
      this.setState({
        currentTab: null,
      })
    } else if (activeTab === 0) {
      if (this.state.closestCentres.length === 0) {
        // Prevents geolocation being run multiple times
        this.centreNearMe()
      }
      this.setState({
        currentTab: activeTab,
      })
    } else if (activeTab === 1) {
      this.showAllCentres()
      this.setState({
        loading: false,
        currentTab: activeTab,
      })
    }
  }
  render() {
    const {
      searchString,
      loading,
      closestCentres,
      centres,
      currentTab,
      disabledTabVal,
    } = this.state
    const { homePageInfo, angleClass, isMobile } = this.props
    const NUMBER_OF_CENTRES = 3
    const getCentres =
      currentTab === 1 || searchString.length > 0
        ? centres
        : closestCentres.slice(0, NUMBER_OF_CENTRES)
    const tabHeadings = ['Nearby', 'All centres']

    return (
      <div className={styles.CentreSelectorBody}>
        <CentreSelectorDevice homePageInfo={homePageInfo} isMobile={isMobile} />
        <div className={classNames(angleClass, styles.CentreSelector)}>
          <Container
            key="centreSelector"
            type="large"
            className={containerStyle.container}
          >
            <div className={styles.wrapper}>
              <div className={styles.CentreSearchWrap}>
                <Icon type="search" className={styles.CentreSearchIcon} />
                <form
                  action="#" /* no-op but makes iOS use 'SEARCH' as return key type */
                  className={
                    this.props.isNZ || searchString
                      ? styles.CentreFormSearch
                      : styles.CentreForm
                  }
                  onSubmit={e => e.preventDefault()}
                >
                  <input
                    autoCorrect="off"
                    type="search"
                    name="centreSearch"
                    aria-label="Find a centre"
                    placeholder="Find a centre"
                    id="centreSearch"
                    data-test-id="centre-search"
                    className={styles.CentreSearchInput}
                    value={this.state.searchString}
                    onChange={this.updateSearchString}
                    onKeyPress={this.handleKeyPress}
                    onFocus={this.handleSearchStarted}
                  />
                </form>
                {!this.props.isNZ && !searchString && (
                  <TabsHeader
                    fullWidth
                    headings={tabHeadings}
                    selectedTab={currentTab}
                    disabledTabVal={disabledTabVal}
                    onTabHeadingClick={e => this.tabBtn(e)}
                  />
                )}
              </div>
              <GridSelector className={styles.GridSelector}>
                {(currentTab !== null || searchString.length > 0) && (
                  <React.Fragment>
                    {loading ? (
                      <GridSelectorItem
                        className={styles.CentreSelectorItem}
                        key="Loading"
                        title="Loading..."
                        subtitle="Please wait"
                        linkUrl=""
                      />
                    ) : (
                      getCentres.map(centre => {
                        const storeName =
                          centre.suburb === centre.title
                            ? centre.state
                            : `${centre.suburb}, ${centre.state}`
                        return (
                          <GridSelectorItem
                            className={styles.CentreSelectorItem}
                            key={centre.title}
                            title={centre.title}
                            subtitle={storeName}
                            linkUrl={`/${centre.slug}`}
                            dataTestTitle="centre-name"
                            dataTestSubtitle="centre-suburb"
                            linkElement={props => (
                              <Link
                                data-test-id="centre-search-result"
                                {...props}
                                to={`/${centre.slug}`}
                                onClick={() => this.handleCentreSelect(centre)}
                              />
                            )}
                          />
                        )
                      })
                    )}
                  </React.Fragment>
                )}
              </GridSelector>
            </div>
          </Container>
        </div>
      </div>
    )
  }
}

CentreSelector.propTypes = {
  angleClass: PropTypes.string,
  centres: PropTypes.arrayOf(PropTypes.object).isRequired,
  homePageInfo: PropTypes.object,
  isNZ: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default CentreSelector
